// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { resolveSupabaseOrigin } from './resolveSupabaseOrigin';

// Same origin as Edge Function `fetch()` helpers — see `resolveSupabaseOrigin.ts`
export const resolvedSupabaseUrl = resolveSupabaseOrigin();
const SUPABASE_PUBLISHABLE_KEY =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhjdmxpamNoa21oam9uaGZpbGRtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc2MDY3MDEsImV4cCI6MjA2MzE4MjcwMX0.3_FZWbV4qCqs1xQmh0Hws83xQxofSApzVRScSCEi9Pg';

/** Same anon key as `createClient` — use for raw `fetch` to Edge Functions (`apikey` header). */
export const resolvedSupabasePublishableKey = SUPABASE_PUBLISHABLE_KEY;

console.info('[ENV CHECK]', {
  url: !!resolvedSupabaseUrl,
  key: !!SUPABASE_PUBLISHABLE_KEY,
  fromEnv: !!import.meta.env.VITE_SUPABASE_URL,
});

if (!resolvedSupabaseUrl || !SUPABASE_PUBLISHABLE_KEY) {
  console.error(
    '[Supabase] Missing configuration — app will run in degraded mode'
  );
}

// Guest session key constant
const GUEST_SESSION_KEY = 'guest_session';

/**
 * Get the current guest ID from storage (if exists)
 * This is called DYNAMICALLY on each request via the fetch wrapper,
 * NOT frozen at init time — prevents 42501 RLS errors.
 */
const getGuestId = (): string => {
  try {
    const raw = localStorage.getItem(GUEST_SESSION_KEY);
    if (raw) {
      const session = JSON.parse(raw);
      const id =
        session?.guestId ||
        session?.guest_id ||
        session?.data?.guestId ||
        session?.data?.guest_id ||
        session?.value?.guestId ||
        session?.value?.guest_id;
      if (id) return id;
    }
  } catch {
    // Ignore parse errors
  }
  return '';
};

/** Get the guest signature from storage (if exists) */
const getGuestSignature = (): string => {
  try {
    const raw = localStorage.getItem(GUEST_SESSION_KEY);
    if (raw) {
      const session = JSON.parse(raw);
      return (
        session?.signature ||
        session?.data?.signature ||
        session?.value?.signature ||
        ''
      );
    }
  } catch {
    // Ignore parse errors
  }
  return '';
};

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(
  resolvedSupabaseUrl,
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      storage: localStorage,
      persistSession: true,
      autoRefreshToken: true,
    },
    global: {
      // Use a function to dynamically resolve x-guest-id on EVERY request
      // Prevents 42501 RLS errors when guest ID wasn't yet generated
      // Also adds AbortController timeout to prevent hanging connections
      // that exhaust the browser's 6-connection-per-host limit.
      fetch: (url: RequestInfo | URL, options?: RequestInit) => {
        const guestId = getGuestId();
        const guestSig = getGuestSignature();
        const headers = new Headers(options?.headers);
        if (guestId) {
          headers.set('x-guest-id', guestId);
          if (guestSig) {
            headers.set('x-guest-signature', guestSig);
          }
        }

        // Abort hanging requests after 15s to free connection slots.
        // Previous value was 30s, but that's longer than the UI safety
        // timeout (12s), meaning users see errors before the fetch fails.
        // 15s gives enough headroom for browser connection queuing while
        // still failing fast enough for React Query retries to complete.
        const controller = new AbortController();
        const existingSignal = options?.signal;
        const timeoutId = setTimeout(() => controller.abort(), 15_000);

        // If caller already provided a signal, respect it too
        if (existingSignal) {
          existingSignal.addEventListener('abort', () => controller.abort());
        }

        const urlStr =
          typeof url === 'string'
            ? url
            : url instanceof URL
              ? url.href
              : '(Request)';
        const shortUrl = urlStr.includes('supabase.co')
          ? urlStr.split('supabase.co')[1]?.substring(0, 60)
          : urlStr.substring(0, 80);
        console.info(`[SupabaseFetch] → ${shortUrl}`);

        return fetch(url, {
          ...options,
          headers,
          signal: controller.signal,
        })
          .then((response) => {
            clearTimeout(timeoutId);
            console.info(`[SupabaseFetch] ← ${response.status} ${shortUrl}`);

            // Log 401/403 for debugging but do NOT wipe auth tokens.
            // A 403 on an admin-only table is EXPECTED for non-admin users
            // and should not destroy the entire session.
            // Only supabase.auth.signOut() should clear auth state.
            if (response.status === 401 || response.status === 403) {
              console.warn(
                `[SupabaseFetch] ${response.status} on ${shortUrl} — RLS denied (expected for restricted tables)`
              );
            }

            return response;
          })
          .catch((err) => {
            clearTimeout(timeoutId);
            console.error(`[SupabaseFetch] ✘ ${shortUrl}`, err?.message || err);
            throw err;
          });
      },
    },
    realtime: {
      params: {
        eventsPerSecond: 2,
      },
      timeout: 30000,
      heartbeatIntervalMs: 30000,
    },
  }
);

// NOTE: createGuestClient was REMOVED to prevent "Multiple GoTrueClient instances" warnings.
// The main `supabase` client already resolves x-guest-id dynamically on every request
